import { useCallback } from 'react';

import { isApiError, makeApiReloading } from 'scenes/common/helpers/apiData';
import { Excess } from 'scenes/common/types';
import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';

import {
  MotorFunnelState,
  MarketAgreedValue,
  MotorFormAnswer,
  MotorApiResponses,
  ReferenceData,
} from 'scenes/motor/types';
import { setOptionalExtras } from 'scenes/motor/actions';
import { quoteApi } from 'scenes/motor/api';

export const useCoverOptionActions = (
  handleAnswer: (f: MotorFormAnswer) => void,
  onApiResponse: (data: Partial<MotorApiResponses>) => void,
  referenceData: ReferenceData
) => {
  const funnelState = useFunnelState<MotorFunnelState>();

  const handleQuoteResponse = useCallback(
    (quoteDetails) => {
      onApiResponse({
        quoteDetails,
      });
      return quoteDetails;
    },
    [onApiResponse]
  );

  const onRequestVehicleValue = useCallback(
    (agreedVehicleValue: string) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateVehicleSumInsured(
          funnelState,
          agreedVehicleValue,
          MarketAgreedValue.Agreed,
          referenceData
        )
        .then(handleQuoteResponse);
    },
    [onApiResponse, funnelState, referenceData, handleQuoteResponse]
  );

  const onResetToMarketValue = useCallback(() => {
    const agreedVehicleValue = '0';
    onApiResponse({
      quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
    });
    quoteApi()
      .updateVehicleSumInsured(
        funnelState,
        agreedVehicleValue,
        MarketAgreedValue.Market,
        referenceData
      )
      .then(handleQuoteResponse);
  }, [onApiResponse, funnelState, referenceData, handleQuoteResponse]);

  const onUpdateExcess = useCallback(
    (newExcess: Excess) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateExcess(funnelState, newExcess, referenceData)
        .then(handleQuoteResponse);
    },
    [onApiResponse, funnelState, referenceData, handleQuoteResponse]
  );

  const onUpdateOptionalExtras = useCallback(
    (selectedExtras: string[]) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updateOptionalExtras(funnelState, selectedExtras, referenceData)
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isApiError(quoteDetails)) {
            handleAnswer(setOptionalExtras(selectedExtras));
          }
        });
    },
    [
      onApiResponse,
      funnelState,
      referenceData,
      handleQuoteResponse,
      handleAnswer,
    ]
  );

  return {
    onRequestVehicleValue,
    onResetToMarketValue,
    onUpdateExcess,
    onUpdateOptionalExtras,
  };
};

export default useCoverOptionActions;
